import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Box, Typography } from '@mui/material'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Calendar, Loader2, Target, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { UnderlineTabs } from '@/components/ui/underline-tabs'
import PageHeader from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { formatNumberSmart, formatDate as formatSharedDate } from '@/lib/utils'

const formatDate = (value) => formatSharedDate(value, { fallback: 'Not set' })

const formatNumber = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number') return `${formatNumberSmart(value, 2)}${suffix}`
  const num = Number(value)
  if (Number.isNaN(num)) return `${value}${suffix}`
  return `${formatNumberSmart(num, 2)}${suffix}`
}

const metricRow = (items) => (
  <div className="divide-y divide-slate-100">
    {items.map((item) => (
      <div key={item.label} className="flex items-center justify-between py-3">
        <p className="text-sm text-slate-500">{item.label}</p>
        <p className="text-sm font-semibold text-slate-900">{item.value}</p>
      </div>
    ))}
  </div>
)

const normalizeGoalSessionsPayload = (payload, activeRefs) => {
  if (!payload) return []
  const raw =
    payload.goalsessions ||
    payload.sessions ||
    payload.data ||
    payload.rows ||
    payload
  if (!Array.isArray(raw)) return []
  return raw.map((session, index) => {
    const weightEntry = Array.isArray(session.weights) ? session.weights[0] : null
    const weightVal = session.weight || session.weight_log || weightEntry?.weight
    const bodyFatVal = session.body_fat || session.bodyfat || session.body_fat_percentage || weightEntry?.bodyfat
    const sessionDate =
      session.session_date ||
      session.date ||
      session.logdate ||
      session.program_start_date ||
      weightEntry?.measured_at ||
      session.created_at
    const sessionProgramId =
      session.program_id ||
      session.programId ||
      session.programid ||
      session.program ||
      session.program_id_pk
    const sessionGoalId =
      session.goal_id ||
      session.goalId ||
      session.goalid ||
      session.goal ||
      session.goal_id_pk
    const activeSessionId = activeRefs?.sessionId
    const activeGoalId = activeRefs?.goalId
    const isActive =
      (activeSessionId && String(session.id) === String(activeSessionId)) ||
      (!activeSessionId && activeGoalId && String(sessionGoalId) === String(activeGoalId))
    return {
      id: session.id || session.session_id || session.goal_session_id || index,
      name: session.name || session.session_name || `Session ${index + 1}`,
      date: sessionDate,
      weight: weightVal,
      bodyFat: bodyFatVal,
      status: isActive ? 'Active' : 'Inactive',
      sessionProgramId,
      goalId: sessionGoalId,
    }
  })
}

const ConsultantProgramDetail = () => {
  const { clientId, programId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  // Supports being mounted under /consultant-clients or /company-clients
  const basePath = `/${location.pathname.split('/')[1] || 'consultant-clients'}`
  const { companyUrl } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [programPayload, setProgramPayload] = useState(null)
  const [sessionsPayload, setSessionsPayload] = useState(null)
  const [activeTab, setActiveTab] = useState('sessions')
  const sessionDetailController = useRef(null)
  const [selectedSessionId, setSelectedSessionId] = useState(null)
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false)
  const [sessionDetailError, setSessionDetailError] = useState('')
  const [sessionDetailData, setSessionDetailData] = useState(null)
  const [showAddSessionModal, setShowAddSessionModal] = useState(false)
  const [createSessionLoading, setCreateSessionLoading] = useState(false)
  const [createSessionError, setCreateSessionError] = useState('')
  const [showCopySessionModal, setShowCopySessionModal] = useState(false)
  const [copyPrograms, setCopyPrograms] = useState([])
  const [copyProgramsLoading, setCopyProgramsLoading] = useState(false)
  const [copyProgramsError, setCopyProgramsError] = useState('')
  const [copyProgramId, setCopyProgramId] = useState('')
  const [copySessionLoading, setCopySessionLoading] = useState(false)
  const [copySessionError, setCopySessionError] = useState('')

  useEffect(() => {
    if (!companyUrl || !clientId || !programId) return
    const controller = new AbortController()
    const loadProgram = async () => {
      setLoading(true)
      setError('')
      try {
        const [programRes, sessionsRes] = await Promise.all([
          apiService.post(`/${companyUrl}/getclientprogramswithgoals`, { client_id: clientId, program_id: programId }, controller.signal),
          apiService.post(`/${companyUrl}/getgoalsessions`, { goal_id: programId }, controller.signal),
        ])
        if (programRes.success) setProgramPayload(programRes.data)
        else setError(programRes.error || 'Unable to load program')
        if (sessionsRes.success) setSessionsPayload(sessionsRes.data)
        else setError((prev) => prev || sessionsRes.error || 'Unable to load sessions')
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to load program details')
      } finally {
        setLoading(false)
      }
    }
    loadProgram()
    return () => controller.abort()
  }, [companyUrl, clientId, programId])

  useEffect(() => {
    return () => {
      if (sessionDetailController.current) sessionDetailController.current.abort()
    }
  }, [])

  const programRecord = useMemo(() => {
    if (!programPayload) return null
    const candidates =
      programPayload.programswithgoals ||
      programPayload.programs ||
      programPayload.data ||
      programPayload.rows ||
      programPayload
    if (Array.isArray(candidates)) {
      const match = candidates.find((item) => String(item.id) === String(programId))
      return match || candidates[0] || null
    }
    return candidates
  }, [programPayload, programId])

  const sessions = useMemo(() => {
    const activeRefs = {
      sessionId:
        programPayload?.user?.session?.id ||
        programPayload?.session?.id ||
        null,
      goalId:
        programPayload?.user?.activeprogram_id ||
        programPayload?.activeprogram_id ||
        null,
    }
    return normalizeGoalSessionsPayload(sessionsPayload, activeRefs)
  }, [sessionsPayload, programPayload])

  const computedProgress = useMemo(() => {
    const startWeight = Number(programRecord?.start_weight || programRecord?.startweight) || null
    const startBodyFat = Number(programRecord?.start_bodyfat || programRecord?.start_body_fat) || null
    const startLeanMass = Number(programRecord?.start_lean_mass) || null
    const startFatMass = Number(programRecord?.start_fat_mass) || null

    const sessionsWithDates = sessions
      .filter((s) => s.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))

    const latestWithWeight = sessionsWithDates.find((s) => s.weight != null && s.weight !== '')
    const latestWithBodyFat = sessionsWithDates.find((s) => s.bodyFat != null && s.bodyFat !== '')

    const currentWeight = latestWithWeight ? Number(latestWithWeight.weight) : null
    const currentBodyFat = latestWithBodyFat ? Number(latestWithBodyFat.bodyFat) : null

    const weightChange = currentWeight != null && startWeight != null ? currentWeight - startWeight : null
    const bodyFatChange = currentBodyFat != null && startBodyFat != null ? currentBodyFat - startBodyFat : null

    const goalWeight = Number(programRecord?.goal_weight) || null
    const goalBodyFat = Number(programRecord?.goal_bodyfat || programRecord?.goal_body_fat) || null

    const currentLeanMass = goalWeight != null && currentBodyFat != null
      ? goalWeight * (1 - currentBodyFat / 100)
      : null
    const currentFatMass = goalWeight != null && currentBodyFat != null
      ? goalWeight * (currentBodyFat / 100)
      : null

    const leanMassChange = currentLeanMass != null && startLeanMass != null ? currentLeanMass - startLeanMass : null
    const fatMassChange = currentFatMass != null && startFatMass != null ? currentFatMass - startFatMass : null

    return { weightChange, bodyFatChange, leanMassChange, fatMassChange }
  }, [sessions, programRecord])

  const handleSessionClick = (session) => {
    if (!companyUrl || !clientId || !programId || !session?.id) return
    setSelectedSessionId(session.id)
    navigate(`${basePath}/${clientId}/sessions/${session.id}`, {
      state: {
        session,
        programId: programRecord?.id || programId,
        goalId: programRecord?.goal_id,
        sessionProgramId: session.sessionProgramId,
      },
    })
  }

  const handleAddSessionOpen = () => {
    setCreateSessionError('')
    setShowAddSessionModal(true)
  }

  const handleAddSessionClose = () => {
    if (createSessionLoading) return
    setShowAddSessionModal(false)
  }

  const handleCreateSession = async () => {
    if (!companyUrl || !clientId || !programId) return
    setCreateSessionLoading(true)
    setCreateSessionError('')
    try {
      const createRes = await apiService.post(`/${companyUrl}/createsession`, { client_id: clientId, goal_id: programId })
      if (!createRes.success) throw new Error(createRes.error || 'Unable to create session')
      let targetSessionId =
        createRes.data?.session_id ||
        createRes.data?.goal_session_id ||
        createRes.data?.id ||
        createRes.data?.sessionId ||
        createRes.data?.data?.id
      let targetSession = null
      const sessionsRes = await apiService.post(`/${companyUrl}/getgoalsessions`, { goal_id: programId })
      if (sessionsRes.success) {
        setSessionsPayload(sessionsRes.data)
        const normalized = normalizeGoalSessionsPayload(sessionsRes.data)
        if (normalized.length > 0) {
          targetSession =
            normalized.find((item) => String(item.id) === String(targetSessionId)) || normalized[normalized.length - 1]
          targetSessionId = targetSession?.id
        }
      } else if (!targetSessionId) {
        throw new Error(sessionsRes.error || 'Unable to refresh sessions')
      }
      if (targetSessionId) {
        setShowAddSessionModal(false)
        handleSessionClick(targetSession || { id: targetSessionId })
      }
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
      setCreateSessionError(err?.message || 'Unable to create session')
    } finally {
      setCreateSessionLoading(false)
    }
  }

  const handleCopySessionOpen = async () => {
    if (!companyUrl || !clientId) return
    setShowCopySessionModal(true)
    setCopyProgramId('')
    setCopyPrograms([])
    setCopyProgramsError('')
    setCopySessionError('')
    setCopyProgramsLoading(true)
    try {
      const res = await apiService.post(`/${companyUrl}/getclientprograms`, { client_id: String(clientId) })
      if (!res.success) throw new Error(res.error || 'Unable to load sessions.')
      const list = res.data?.programs || res.data?.data || res.data || []
      setCopyPrograms(Array.isArray(list) ? list : [])
    } catch (err) {
      setCopyProgramsError(err?.message || 'Unable to load sessions.')
    } finally {
      setCopyProgramsLoading(false)
    }
  }

  const handleCopySessionClose = () => {
    if (copySessionLoading) return
    setShowCopySessionModal(false)
  }

  const handleCopySessionConfirm = async () => {
    if (!companyUrl || !clientId || !programId) return
    if (!copyProgramId) {
      setCopySessionError('Select a session to copy from.')
      return
    }
    const knownSessionIds = new Set(sessions.map((session) => String(session.id)))
    setCopySessionLoading(true)
    setCopySessionError('')
    try {
      const copyRes = await apiService.post(`/${companyUrl}/copysession`, {
        program_id: Number(copyProgramId),
        goal_id: Number(programId),
      })
      if (!copyRes.success) throw new Error(copyRes.error || 'Unable to copy session')

      const copyData = copyRes.data || {}
      const copyDataNested =
        copyData.session ||
        copyData.goalSession ||
        copyData.goalsession ||
        copyData.data ||
        {}
      let targetSessionId =
        copyData.session_id ||
        copyData.goal_session_id ||
        copyData.goalSessionId ||
        copyData.sessionId ||
        copyData.id ||
        copyDataNested.id ||
        copyDataNested.session_id ||
        copyDataNested.goal_session_id
      const targetSessionProgramId =
        copyData.program_id ||
        copyData.programId ||
        copyData.session_program_id ||
        copyDataNested.program_id ||
        copyDataNested.programId

      const sessionsRes = await apiService.post(`/${companyUrl}/getgoalsessions`, { goal_id: programId })
      if (!sessionsRes.success) {
        throw new Error(sessionsRes.error || 'Session copied, but unable to refresh sessions.')
      }
      setSessionsPayload(sessionsRes.data)
      const normalized = normalizeGoalSessionsPayload(sessionsRes.data)
      const newlyAddedSession = normalized.find((session) => !knownSessionIds.has(String(session.id)))
      const targetSessionById = normalized.find((session) => String(session.id) === String(targetSessionId))
      const targetSessionByProgramId = normalized.find(
        (session) => String(session.sessionProgramId) === String(targetSessionProgramId)
      )
      const targetSession =
        targetSessionById ||
        targetSessionByProgramId ||
        newlyAddedSession ||
        normalized[normalized.length - 1] ||
        null
      targetSessionId = targetSession?.id || targetSessionId

      if (!targetSessionId) {
        throw new Error('Session copied, but unable to open it automatically.')
      }

      setShowCopySessionModal(false)
      handleSessionClick(targetSession || { id: targetSessionId })
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
      setCopySessionError(err?.message || 'Unable to copy session')
    } finally {
      setCopySessionLoading(false)
    }
  }

  const programTitle = programRecord?.name || 'Program'
  const dateRange = programRecord?.start_date && programRecord?.estimated_end_date
    ? `${formatDate(programRecord.start_date)} - ${formatDate(programRecord.estimated_end_date)}`
    : 'Schedule pending'
  const startMetrics = [
    { label: 'Start Weight', value: formatNumber(programRecord?.start_weight || programRecord?.startweight, ' lbs') },
    { label: 'Start Body Fat', value: formatNumber(programRecord?.start_bodyfat || programRecord?.start_body_fat, ' %') },
    { label: 'Start Lean Mass', value: formatNumber(programRecord?.start_lean_mass, ' lbs') },
    { label: 'Start Fat Mass', value: formatNumber(programRecord?.start_fat_mass, ' lbs') },
    { label: 'Start Date', value: formatDate(programRecord?.start_date) },
    { label: 'Sessions to Goal', value: formatNumber(programRecord?.sessions_to_goal || programRecord?.sessiontogoal) },
  ]
  const goalMetrics = [
    { label: 'Goal Weight', value: formatNumber(programRecord?.goal_weight, ' lbs') },
    { label: 'Goal Body Fat', value: formatNumber(programRecord?.goal_bodyfat || programRecord?.goal_body_fat, ' %') },
    { label: 'Goal Lean Mass', value: formatNumber(programRecord?.goal_lean_mass, ' lbs') },
    { label: 'Goal Fat Mass', value: formatNumber(programRecord?.goal_fat_mass, ' lbs') },
    { label: 'End Date', value: formatDate(programRecord?.estimated_end_date || programRecord?.end_date) },
    { label: 'Sessions Purchased', value: formatNumber(programRecord?.packagepurchased || programRecord?.sessions_purchased) },
  ]
  const progressMetrics = [
    { label: 'Weight Change', value: formatNumber(computedProgress.weightChange, ' lbs') },
    { label: 'Body Fat Change', value: formatNumber(computedProgress.bodyFatChange, ' %') },
    { label: 'Lean Mass Change', value: formatNumber(computedProgress.leanMassChange, ' lbs') },
    { label: 'Fat Mass Change', value: formatNumber(computedProgress.fatMassChange, ' lbs') },
    { label: 'Sessions To Goal', value: formatNumber(programRecord?.sessions_to_goal || programRecord?.sessiontogoal) },
  ]
  const detailIndicators = [
    { label: 'Meal plans', ready: !!sessionDetailData?.mealPlans },
    { label: 'Nutrition templates', ready: !!sessionDetailData?.nutritionTree },
    { label: 'Static pages', ready: !!sessionDetailData?.staticPages },
    { label: 'Measurements', ready: !!sessionDetailData?.measurements },
    { label: 'Exercise plans', ready: !!sessionDetailData?.sessionExercisePlans },
    { label: 'Exercise categories', ready: !!sessionDetailData?.exerciseCategoryTree },
    { label: 'Exercise templates', ready: !!sessionDetailData?.exerciseTemplateCategories },
    { label: 'Session plans', ready: !!sessionDetailData?.sessionPlans },
    { label: 'Goal sessions', ready: !!sessionDetailData?.goalSessions },
  ]

  return (
    <>
      <Modal open={showCopySessionModal} onClose={handleCopySessionClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(520px, 92vw)',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            Copy session
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            Select which session to copy into this program.
          </Typography>
          <div className="space-y-2">
            <label htmlFor="copy-session-program" className="text-sm font-medium text-slate-700">
              Session to copy
            </label>
            <select
              id="copy-session-program"
              value={copyProgramId}
              onChange={(event) => {
                setCopyProgramId(event.target.value)
                setCopySessionError('')
              }}
              disabled={copyProgramsLoading || copySessionLoading || !copyPrograms.length}
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
            >
              <option value="">
                {copyProgramsLoading
                  ? 'Loading sessions...'
                  : copyPrograms.length
                    ? 'Select a session'
                    : 'No sessions available'}
              </option>
              {copyPrograms.map((program) => (
                <option key={program.id} value={String(program.id)}>
                  {program.name || `Session ${program.id}`}
                  {program.goal?.name ? ` (${program.goal.name})` : ''}
                </option>
              ))}
            </select>
            {copyProgramsError ? <Typography color="error" sx={{ fontSize: 13 }}>{copyProgramsError}</Typography> : null}
            {copySessionError ? <Typography color="error" sx={{ fontSize: 13 }}>{copySessionError}</Typography> : null}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleCopySessionClose} disabled={copySessionLoading}>
              Cancel
            </Button>
            <Button
              className="bg-fitness-primary text-primary-foreground hover:bg-fitness-primary/90"
              onClick={handleCopySessionConfirm}
              disabled={copySessionLoading || !copyProgramId}
            >
              {copySessionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal open={showAddSessionModal} onClose={handleAddSessionClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(440px, 90vw)',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            Create session?
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            Confirm to create a new session for this program.
          </Typography>
          {createSessionError ? (
            <Typography color="error" sx={{ fontSize: 14 }}>
              {createSessionError}
            </Typography>
          ) : null}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleAddSessionClose} disabled={createSessionLoading}>
              Cancel
            </Button>
            <Button
              className="bg-fitness-primary text-primary-foreground hover:bg-fitness-primary/90"
              onClick={handleCreateSession}
              disabled={createSessionLoading}
            >
              {createSessionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </div>
        </Box>
      </Modal>

      <div className="app-page min-h-screen">
        <PageHeader
          backTo={`${basePath}/${clientId}?tab=programs`}
          backLabel="Back to programs"
          title={programTitle}
          subtitle={`${sessions.length} ${sessions.length === 1 ? 'session' : 'sessions'} · ${dateRange}`}
        >
          <Button size="sm" onClick={handleAddSessionOpen}>
            Add session
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopySessionOpen}>
            Copy session
          </Button>
        </PageHeader>

        <div className="w-full space-y-5">
        <UnderlineTabs
          tabs={[
            { id: 'sessions', label: 'Sessions' },
            { id: 'goals', label: 'Goals' },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          label="Program sections"
        />

        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading program details...
          </div>
        )}

        {!loading && error && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-red-600">{error}</CardContent>
          </Card>
        )}

        {!loading && !error && (
          <>
            {activeTab === 'sessions' && (
              <Card className="overflow-hidden">
                <CardHeader className="flex flex-wrap justify-between gap-3 pb-3">
                  <div>
                    <CardTitle className="text-base font-semibold text-slate-900">Sessions</CardTitle>
                    <p className="text-sm text-slate-500">Track every milestone across this program</p>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="overflow-x-auto rounded-[10px] border border-slate-200 bg-white">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-background text-xs text-slate-500">
                        <tr>
                          <th className="px-4 py-3 font-medium">Name</th>
                          <th className="px-4 py-3 font-medium">Session Date</th>
                          <th className="px-4 py-3 font-medium">Weight</th>
                          <th className="px-4 py-3 font-medium">Body Fat</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.length === 0 && (
                          <tr>
                            <td colSpan="5" className="px-4 py-6 text-center text-slate-500">
                              No sessions found for this program.
                            </td>
                          </tr>
                        )}
                        {sessions.map((session) => (
                          <tr
                            key={session.id}
                            onClick={() => handleSessionClick(session)}
                            className={`border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer ${
                              selectedSessionId === session.id ? 'bg-fitness-primary/5 ring-1 ring-inset ring-fitness-primary/30' : ''
                            }`}
                          >
                            <td className="px-4 py-3 font-medium text-slate-900">{session.name}</td>
                            <td className="px-4 py-3 text-slate-700">{formatDate(session.date)}</td>
                            <td className="px-4 py-3 text-slate-700">{formatNumber(session.weight)}</td>
                            <td className="px-4 py-3 text-slate-700">{formatNumber(session.bodyFat)}</td>
                            <td className="px-4 py-3">
                              <Badge>{session.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                    {sessionDetailLoading && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading session data...</span>
                      </div>
                    )}
                    {!sessionDetailLoading && sessionDetailError && (
                      <p className="text-sm text-red-600">{sessionDetailError}</p>
                    )}
                    {!sessionDetailLoading && !sessionDetailError && (
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
                        {selectedSessionId ? (
                          <>
                            <span className="font-semibold text-slate-900">Session data</span>
                            {detailIndicators.map((item) => (
                              <span
                                key={item.label}
                                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ring-1 ring-slate-200 ${
                                  item.ready ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-white text-slate-500'
                                }`}
                              >
                                <span className={`h-2 w-2 rounded-full ${item.ready ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                {item.label}
                              </span>
                            ))}
                          </>
                        ) : (
                          <span>Select a session to load detailed data.</span>
                        )}
                      </div>
                    )}
                  </div> */}
                </CardContent>
              </Card>
            )}

            {activeTab === 'goals' && (
              <div className="grid gap-4 lg:grid-cols-3">
                <Card className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Target className="h-5 w-5 text-fitness-primary" />
                      Starting Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>{metricRow(startMetrics)}</CardContent>
                </Card>

                <Card className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Calendar className="h-5 w-5 text-fitness-primary" />
                      Target Goals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>{metricRow(goalMetrics)}</CardContent>
                </Card>

                <Card className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <TrendingUp className="h-5 w-5 text-fitness-primary" />
                      Progress Tracking
                    </CardTitle>
                  </CardHeader>
                  <CardContent>{metricRow(progressMetrics)}</CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  )
}

export default ConsultantProgramDetail
